/**
 * Requests cancelled on purpose, reported as `AbortError` / `CanceledError`.
 *
 * These come from `AbortController.abort()` — a fetch timeout inside the Supabase
 * client, an axios cancellation, or a `useEffect` cleanup dropping an in-flight
 * request when the user navigates away. The abort is the intended outcome, so the
 * rejection describes normal cancellation rather than a fault to investigate.
 *
 * Only unambiguous abort phrasings belong in `ABORT_MESSAGES`. A loose token such
 * as `canceled` would also swallow real domain failures ("subscription canceled
 * unexpectedly"), so anything vaguer is left to the name checks below.
 */
const ABORT_MESSAGES = [
  'signal is aborted without reason',
  'The user aborted a request',
  'The operation was aborted',
];

/** True when `error` is a deliberately cancelled request rather than a failure. */
export function isAbortedRequestNoise(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  // Prefer `.name` over deprecated `DOMException.code` (ABORT_ERR).
  if (error.name === 'AbortError' || error.name === 'CanceledError') {
    return true;
  }
  const message = error.message || '';
  return ABORT_MESSAGES.some((pattern) => message.includes(pattern));
}
